import React from "react";
import { MenuItem, Select, Box } from "@mui/material";
import i18n, { loadNamespace } from "../i18n";
import { useTranslation } from "react-i18next";
import { AVAILABLE_LANGS } from "../i18n/languages";
import axios from "../api";

export default function LanguageSwitcher({
  size = "small",
  persistBackend = true,
  disablePortal = false,
  themeColor = false,
  textColor = null,
  iconColor = null,
  compact = false,
}) {
  const { t } = useTranslation();
  const [lang, setLang] = React.useState(i18n.language);
  const mounted = React.useRef(false);

  React.useEffect(() => {
    // ensure public namespace (UI strings) is loaded for current language
    loadNamespace("public");
    const h = (l) => {
      setLang(l);
    };
    i18n.on("languageChanged", h);
    return () => i18n.off("languageChanged", h);
  }, []);

  const change = async (e) => {
    const newLang = e.target.value;
    setLang(newLang);
    i18n.changeLanguage(newLang);
    try {
      localStorage.setItem("app.lang", newLang);
    } catch {}
    // Spróbuj zapisać preferencję backend jeśli użytkownik zalogowany
    if (persistBackend) {
      try {
        await axios.put("/me/preferences", { preferred_locale: newLang });
      } catch {}
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", ml: 2, mr: 1 }}>
      <Select
        size={size}
        value={lang}
        onChange={change}
        inputProps={{
          "aria-label": t("aria.languageSwitcher", {
            ns: "public",
            defaultValue: "Wybór języka",
          }),
        }}
        variant="standard"
        disableUnderline
        MenuProps={{
          disablePortal,
          PaperProps: {
            sx: {
              mt: 0.75,
              minWidth: 96,
              border: "1px solid rgba(31, 41, 55, 0.12)",
              borderRadius: 2,
              boxShadow: "0 12px 30px rgba(31, 41, 55, 0.16)",
              overflow: "hidden",
            },
          },
        }}
        sx={(theme) => ({
          color:
            textColor || (themeColor ? theme.palette.text.primary : "inherit"),
          fontWeight: 600,
          minWidth: compact ? 58 : 48,
          height: compact ? 32 : "auto",
          border: compact ? "1px solid rgba(31, 41, 55, 0.12)" : "none",
          borderRadius: compact ? "10px" : 0,
          backgroundColor: compact
            ? "rgba(255, 255, 255, 0.78)"
            : "transparent",
          boxShadow: compact ? "0 6px 18px rgba(31, 41, 55, 0.09)" : "none",
          backdropFilter: compact ? "blur(12px)" : "none",
          WebkitBackdropFilter: compact ? "blur(12px)" : "none",
          transition: "border-color 160ms ease, box-shadow 160ms ease",
          "&:hover": compact
            ? {
                borderColor: "rgba(31, 41, 55, 0.24)",
                boxShadow: "0 8px 22px rgba(31, 41, 55, 0.12)",
              }
            : {},
          "&.Mui-focused": compact
            ? {
                borderColor: theme.palette.primary.main,
                boxShadow: `0 0 0 4px ${theme.palette.primary.main}22`,
              }
            : {},
          "& .MuiSelect-select": {
            py: compact ? 0 : 0.5,
            px: compact ? 1 : 0,
            pr: compact ? "23px !important" : undefined,
            minHeight: compact ? "unset !important" : undefined,
            display: "flex",
            alignItems: "center",
            fontSize: compact ? "0.75rem" : undefined,
            letterSpacing: compact ? "0.04em" : undefined,
          },
          "& .MuiSelect-icon": {
            top: "50%",
            transform: "translateY(-50%)",
            color:
              iconColor ||
              textColor ||
              (themeColor ? theme.palette.text.primary : "inherit"),
            right: compact ? 6 : undefined,
          },
          "&:before,&:after": { display: "none" },
        })}
        renderValue={(v) =>
          (AVAILABLE_LANGS.find((l) => l.code === v) || {}).label || v
        }
      >
        {AVAILABLE_LANGS.map((l) => (
          <MenuItem
            key={l.code}
            value={l.code}
            sx={(theme) => ({
              fontWeight: 600,
              minHeight: 36,
              fontSize: compact ? "0.75rem" : undefined,
              color:
                l.code === lang
                  ? theme.palette.text.primary
                  : theme.palette.text.secondary,
              "&.Mui-selected": {
                color: theme.palette.primary.main,
                backgroundColor: theme.palette.primary.main + "12",
              },
              "&.Mui-selected:hover": {
                backgroundColor: theme.palette.primary.main + "1c",
              },
            })}
          >
            {l.label}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}
